import {
    getFloorLayoutsService,
    getSectionsService,
    getSeatsService,
    searchSeatsService,
    getBookingsService,
    createBookingService,
    cancelBookingService,
    createRecurringBookingService,
} from "../services/userBookings";

// ─── API ↔ Local mapping ──────────────────────────────────────────────────────

const API_TYPE_TO_LOCAL = {
    Bay: "rectangle",
    Cabin: "cabin",
    "Conference Room": "conference",
    Cube: "cubicle",
};
const API_SEAT_STATUS = { Active: "available", Maintenance: "blocked", Inactive: "blocked" };

const mapSection = (s) => ({
    id: s._id,
    name: s.name,
    type: API_TYPE_TO_LOCAL[s.type] || "rectangle",
    x: (s.global_position?.col ?? 0) + 1,
    y: (s.global_position?.row ?? 0) + 1,
    width: s.dimensions?.width || 1,
    height: s.dimensions?.height || 1,
    seatRows: Number(s.seat_dimensions?.rows) || 2,
    seatColumns: Number(s.seat_dimensions?.columns) || 6,
    status: s.status || "Active",
    seats: [],
});

const mapSeat = (s) => ({
    id: s._id,
    label: s.seat_number,
    row: (s.local_position?.row ?? 0) + 1,
    column: (s.local_position?.col ?? 0) + 1,
    type: s.seat_capability?.toLowerCase() || "laptop",
    status: API_SEAT_STATUS[s.status] || "available",
    occupancyState: s.occupancy_state?.toLowerCase() || null,
    bookingType: s.booking_details?.type?.toLowerCase() || null,
    restrictionType: s.booking_details?.restriction_type?.toLowerCase() || null,
    sectionId: s.section_id?._id || s.section_id,
    permanentUserId: s.permanent_user_id || null,
});

const formatDateWithTimezone = (dateStr, tz = "Asia/Kolkata") => {
    if (!dateStr) return null;
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr.slice(0, 10);
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(d);
        let year, month, day;
        parts.forEach(part => {
            if (part.type === 'year') year = part.value;
            if (part.type === 'month') month = part.value;
            if (part.type === 'day') day = part.value;
        });
        return `${year}-${month}-${day}`;
    } catch (e) {
        return dateStr.slice(0, 10);
    }
};

const mapBooking = (b, apiSeats = [], timezone = "Asia/Kolkata") => {
    let sectionId = b.section_id;
    if (!sectionId) {
        const seat = apiSeats.find((s) => s._id === (b.seat_id?._id || b.seat_id));
        sectionId = seat?.section_id?._id || seat?.section_id;
    }
    const rawStatus = b.status?.toLowerCase();
    const status = rawStatus === "reserved" || rawStatus === "active" || rawStatus === "booked"
        ? "active"
        : rawStatus || "active";
    const bookingType = b.booking_details?.type?.toLowerCase() || b.type?.toLowerCase() || "one-time";
    const isPermanent = bookingType === "permanent";

    const formattedDate = formatDateWithTimezone(b.start_time, timezone);
    const formattedCreatedAt = formatDateWithTimezone(b.created_at, timezone);

    return {
        id: b.booking_id || b._id,
        seatId: b.seat_id?._id || b.seat_id,
        sectionId,
        bookedFor: b.user?._id || b.user_id,
        date: isPermanent ? "permanent" : (formattedDate || null),
        seatNumber: b.seat_id?.seat_number || null,
        bookingType,
        status,
        note: b.note || "",
        startTime: formattedDate || b.start_time || null,
        createdAt: formattedCreatedAt || b.created_at || new Date().toISOString(),
        updatedAt: b.updated_at || new Date().toISOString(),
    };
};

const DEFAULT_OFFICE = { name: "Toobler HQ", gridColumns: 12, gridRows: 10 };

// ─── Model ────────────────────────────────────────────────────────────────────

export const userBookings = {
    state: {
        workspace: {
            office: DEFAULT_OFFICE,
            sections: [],
            bookings: [],
            layoutId: null,
            isLaptopFull: false,
            updatedAt: null,
        },
        seatSearchResults: [],
        availableSeats: null,
        reservedSeatIds: [],
        blockedSeatIds: [],
        permanentSeatIds: [],
        recurringSeatIds: [],
        loading: false,
    },

    reducers: {
        setWorkspace: (state, workspace) => ({ ...state, workspace }),
        setSeatSearchResults: (state, seatSearchResults) => ({ ...state, seatSearchResults }),
        setAvailableSeats: (state, availableSeats) => ({ ...state, availableSeats }),
        setReservedSeatIds: (state, reservedSeatIds) => ({ ...state, reservedSeatIds }),
        setBlockedSeatIds: (state, blockedSeatIds) => ({ ...state, blockedSeatIds }),
        setPermanentSeatIds: (state, permanentSeatIds) => ({ ...state, permanentSeatIds }),
        setRecurringSeatIds: (state, recurringSeatIds) => ({ ...state, recurringSeatIds }),
        setIsLaptopFull: (state, isLaptopFull) => ({ ...state, workspace: { ...state.workspace, isLaptopFull } }),
        setLoading: (state, loading) => ({ ...state, loading }),
    },

    effects: () => ({

        async loadWorkspace() {
            this.setLoading(true);
            try {
                const layoutsRes = await getFloorLayoutsService();
                const layouts = layoutsRes.data?.data || layoutsRes.data || [];

                if (!layouts.length) {
                    this.setWorkspace({ office: DEFAULT_OFFICE, sections: [], bookings: [], updatedAt: new Date().toISOString() });
                    return;
                }

                const layout = layouts[0];
                const layoutId = layout._id;

                const [sectionsRes, seatsRes, bookingsRes] = await Promise.all([
                    getSectionsService({ layout_id: layoutId }),
                    getSeatsService(),
                    getBookingsService(),
                ]);

                const sectionsPayload = sectionsRes.data?.data;
                const rawSections = sectionsPayload?.sections || sectionsPayload || sectionsRes.data;
                const apiSections = Array.isArray(rawSections) ? rawSections : [];
                const rawSeats = seatsRes.data?.data || seatsRes.data;
                const apiSeats = Array.isArray(rawSeats) ? rawSeats : [];
                const rawBookings = bookingsRes.data?.data || bookingsRes.data;
                const apiBookings = Array.isArray(rawBookings) ? rawBookings : [];

                const seatsBySection = apiSeats.reduce((acc, seat) => {
                    const mapped = mapSeat(seat);
                    if (!acc[mapped.sectionId]) acc[mapped.sectionId] = [];
                    acc[mapped.sectionId].push(mapped);
                    return acc;
                }, {});

                const sections = apiSections.map((s) => {
                    const mapped = mapSection(s);
                    mapped.seats = seatsBySection[mapped.id] || [];
                    return mapped;
                });

                const timezone = sectionsPayload?.timezone || sectionsPayload?.layout_id?.timezone || "Asia/Kolkata";
                const bookings = apiBookings.map((b) => mapBooking(b, apiSeats, timezone));

                const office = {
                    name: layout.name || DEFAULT_OFFICE.name,
                    gridColumns: layout.grid_max_width || sectionsPayload?.layout_id?.grid_max_width || DEFAULT_OFFICE.gridColumns,
                    gridRows: layout.grid_max_height || sectionsPayload?.layout_id?.grid_max_height || DEFAULT_OFFICE.gridRows,
                };

                const isLaptopFull = Boolean(sectionsPayload?.is_laptop_full);

                this.setWorkspace({ office, sections, bookings, layoutId, isLaptopFull, updatedAt: new Date().toISOString() });
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        // ─── Seats ─────────────────────────────────────────────────────────

        async searchSeats({ layoutId, startTime, endTime, seatCapability }) {
            this.setLoading(true);
            try {
                const params = {
                    layout_id: layoutId,
                    start_time: startTime,
                    end_time: endTime,
                };
                if (seatCapability) params.seat_capability = seatCapability;
                const responseData = await searchSeatsService(params);
                const results = responseData.data?.data || responseData.data || [];
                this.setSeatSearchResults(results);
                return results;
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        /**
         * Fetch real-time seat availability for a specific date.
         * Calls GET /seats/search with the given date expanded to a full-day window.
         * Returns a Set-like array of available seat IDs that UI can cross-reference.
         */
        async loadAvailableSeats({ date }, rootState) {
            this.setLoading(true);
            try {
                const layoutId = rootState.userBookings.workspace?.layoutId;
                if (!layoutId) {
                    this.setLoading(false);
                    return;
                }

                const startTime = new Date(`${date}T00:00:00`).toISOString();
                const endTime = new Date(`${date}T23:59:59`).toISOString();

                const responseData = await searchSeatsService({
                    layout_id: layoutId,
                    start_time: startTime,
                    end_time: endTime,
                });

                const payload = responseData.data?.data || responseData.data;
                const sections = payload?.sections || payload || [];
                const availableSeatIds = [];
                const reservedSeatIds = [];
                const blockedSeatIds = [];
                const permanentSeatIds = [];
                const recurringSeatIds = [];

                // Update isLaptopFull from top-level payload for the specific date queried
                const isLaptopFull = Boolean(payload?.is_laptop_full);
                this.setIsLaptopFull(isLaptopFull);

                if (Array.isArray(sections)) {

                    sections.forEach((section) => {
                        const seats = section.seats || [];
                        seats.forEach((s) => {
                            if (!s._id) return;
                            const occupancyState = s.occupancy_state?.toLowerCase();
                            const restrictionType = s.booking_details?.restriction_type?.toLowerCase();
                            const bookingType = s.booking_details?.type?.toLowerCase();
                            if (restrictionType === "block" || bookingType === "block") {
                                blockedSeatIds.push(s._id);
                                return;
                            }
                            if (bookingType === "permanent") {
                                permanentSeatIds.push(s._id);
                                return;
                            }
                            if (bookingType === "recurring") {
                                recurringSeatIds.push(s._id);
                                return;
                            }
                            if (occupancyState === "booked" || occupancyState === "reserved") {
                                reservedSeatIds.push(s._id);
                                return;
                            }
                            availableSeatIds.push(s._id);
                        });
                    });
                }

                this.setAvailableSeats(availableSeatIds);
                this.setReservedSeatIds(reservedSeatIds);
                this.setBlockedSeatIds(blockedSeatIds);
                this.setPermanentSeatIds(permanentSeatIds);
                this.setRecurringSeatIds(recurringSeatIds);
                return availableSeatIds;
            } catch (err) {
                console.log("loadAvailableSeats error", err);
                // On error, clear available seats so UI falls back to conservative "unknown"
                this.setAvailableSeats(null);
                this.setReservedSeatIds([]);
                this.setBlockedSeatIds([]);
                this.setPermanentSeatIds([]);
                this.setRecurringSeatIds([]);
            } finally {
                this.setLoading(false);
            }
        },

        // ─── Bookings ──────────────────────────────────────────────────────

        async createMultipleBookings(payload) {
            const { dates, seatId } = payload;

            this.setLoading(true);
            try {
                for (const date of dates) {
                    await createBookingService({
                        seat_id: seatId,
                        start_time: new Date(`${date}T09:00:00Z`).toISOString(),
                        end_time: new Date(`${date}T18:00:00Z`).toISOString(),
                        type: "One-time",
                    });
                }
                await this.loadWorkspace();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async cancelBooking({ bookingId, reason }) {
            this.setLoading(true);
            try {
                await cancelBookingService(bookingId, { reason: reason || "Cancelled by user" });
                await this.loadWorkspace();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },

        async cancelMultipleBookings({ bookingIds, reason }) {
            this.setLoading(true);
            try {
                for (const bookingId of bookingIds) {
                    await cancelBookingService(bookingId, { reason: reason || "Cancelled by user" });
                }
                await this.loadWorkspace();
            } catch (err) {
                console.log("err", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
    }),
};
